import React from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plane, Users, HeartPulse, FileText, Hotel, ShieldCheck, AlertTriangle, TrendingUp
} from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import OrgChart from "@/components/OrgChart";

const KpiCard = ({ icon: Icon, label, value, accent, sub, testid }) => (
  <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="mt-2 text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{value ?? "—"}</div>
          {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent}`}><Icon className="w-5 h-5" /></div>
      </div>
    </CardContent>
  </Card>
);

const InventoryPie = ({ title, data, testid }) => {
  const COLORS = ["#10B981", "#F59E0B", "#EF4444"];
  const chartData = [
    { name: "Tersedia", value: data.available },
    { name: "Stok Rendah", value: data.low },
    { name: "Habis", value: data.out },
  ];
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-700">{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="h-40">
          <ResponsiveContainer><PieChart>
            <Pie data={chartData} innerRadius={40} outerRadius={60} paddingAngle={2} dataKey="value">
              {chartData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
            </Pie>
            <Tooltip />
          </PieChart></ResponsiveContainer>
        </div>
        <div className="flex justify-around text-xs">
          {chartData.map((d, i) => (
            <div key={d.name} className="text-center">
              <div className="font-bold text-slate-800">{d.value}</div>
              <div className="flex items-center gap-1 text-slate-500"><span className="w-2 h-2 rounded-full" style={{background:COLORS[i]}} />{d.name}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const OrgNode = ({ title, name, tone = "green" }) => {
  const bg = tone === "green" ? "bg-[#00A651] text-white" : tone === "gold" ? "bg-[#FFC72C] text-[#083B1E]" : "bg-white text-slate-800 border border-slate-200";
  return (
    <div className={`rounded-xl px-4 py-3 shadow-sm ${bg}`}>
      <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80">{title}</div>
      <div className="font-semibold text-sm mt-1">{name}</div>
    </div>
  );
};

export default function DashboardPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => (await apiClient.get("/dashboard/stats")).data,
  });
  const { user, isAdmin } = useAuth();
  const { data: logs } = useQuery({
    queryKey: ["recent-logs"],
    queryFn: async () => (await apiClient.get("/activity-logs", { params: { limit: 8 }})).data,
    enabled: isAdmin(),
  });

  const kpi = stats?.kpi || {};
  const inv = stats?.inventory_status || { pilot:{available:0,low:0,out:0}, fa:{available:0,low:0,out:0}, medical:{available:0,low:0,out:0}};

  const stockBar = [
    { name: "Pilot", stok: kpi.pilot_total_stock || 0 },
    { name: "FA", stok: kpi.fa_total_stock || 0 },
    { name: "Medis", stok: kpi.medical_total_stock || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="fodm-gradient rounded-2xl p-6 lg:p-8 text-white relative overflow-hidden">
        <div className="absolute right-0 top-0 opacity-10"><Plane className="w-64 h-64 -mt-8 -mr-8" /></div>
        <div className="relative">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-white/80">E-System JKTOFQG</div>
          <h1 className="mt-2 text-3xl lg:text-4xl font-bold" style={{fontFamily:"Outfit"}}>Flight Operation Group</h1>
          <p className="mt-2 text-white/85 max-w-3xl text-sm">Pusat Manajemen Inventaris Air Crew, Dokumen Operasional dan Kemitraan Flight Operation Group</p>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({length:8}).map((_,i)=><Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={Plane} label="Perlengkapan Pilot" value={kpi.pilot_items} sub={`${kpi.pilot_total_stock} unit stok`} accent="bg-emerald-100 text-[#00A651]" testid="kpi-pilot" />
          <KpiCard icon={Users} label="Perlengkapan FA" value={kpi.fa_items} sub={`${kpi.fa_total_stock} unit stok`} accent="bg-amber-100 text-[#F59E0B]" testid="kpi-fa" />
          <KpiCard icon={HeartPulse} label="Perlengkapan Medis" value={kpi.medical_items} sub={`${kpi.medical_total_stock} unit stok`} accent="bg-rose-100 text-rose-600" testid="kpi-medical" />
          <KpiCard icon={FileText} label="Dokumen Aktif" value={kpi.active_documents} sub={`${kpi.documents} total dokumen`} accent="bg-sky-100 text-sky-600" testid="kpi-docs" />
          <KpiCard icon={Hotel} label="Mitra Hotel" value={kpi.hotels} sub="Direktori aktif" accent="bg-violet-100 text-violet-600" testid="kpi-hotels" />
          <KpiCard icon={ShieldCheck} label="PKS Hotel" value={kpi.hotel_agreements} sub={`${kpi.expiring_agreements} akan berakhir`} accent="bg-emerald-100 text-[#00A651]" testid="kpi-pks" />
          <KpiCard icon={AlertTriangle} label="Peringatan Kadaluarsa" value={kpi.expiring_agreements} sub="PKS mendekati expiry" accent="bg-yellow-100 text-yellow-700" testid="kpi-alerts" />
          <KpiCard icon={TrendingUp} label="Total Item Inventaris" value={(kpi.pilot_items||0)+(kpi.fa_items||0)+(kpi.medical_items||0)} sub="Pilot + FA + Medis" accent="bg-slate-100 text-slate-700" testid="kpi-total" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <InventoryPie title="Status Inventaris Pilot" data={inv.pilot} testid="pie-pilot" />
        <InventoryPie title="Status Inventaris FA" data={inv.fa} testid="pie-fa" />
        <InventoryPie title="Status Inventaris Medis" data={inv.medical} testid="pie-medical" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader><CardTitle className="text-base font-semibold text-slate-800" style={{fontFamily:"Outfit"}}>Total Stok Per Kategori</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer><BarChart data={stockBar}>
                <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Legend />
                <Bar dataKey="stok" fill="#00A651" radius={[8,8,0,0]} name="Total Stok" />
              </BarChart></ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base font-semibold text-slate-800" style={{fontFamily:"Outfit"}}>Aktivitas Terkini</CardTitle></CardHeader>
          <CardContent className="space-y-3 max-h-64 overflow-y-auto" data-testid="recent-activity">
            {(logs || []).length === 0 && <div className="text-xs text-slate-500 italic">Belum ada aktivitas.</div>}
            {(logs || []).map((l) => (
              <div key={l.id} className="flex gap-3 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-[#00A651] mt-1.5" />
                <div className="flex-1">
                  <div className="text-slate-800"><span className="font-semibold">{l.user_name}</span> {l.description.toLowerCase()}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{l.module} · {new Date(l.timestamp).toLocaleString("id-ID")}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base font-semibold text-slate-800" style={{fontFamily:"Outfit"}}>Struktur Organisasi</CardTitle></CardHeader>
        <CardContent>
          <OrgChart />
        </CardContent>
      </Card>
    </div>
  );
}
